import React from 'react';
// Rimosso useState, ChevronDown, ChevronRight
import type { LayerBase, LayerStyleProperties } from '../../../../store/types';
import { StylePropertyInput } from '../../../../common/StylePropertyInput';
import type { StyleValue } from '../../../../common/StylePropertyInput';

interface StyleEditorSectionProps {
  layer: LayerBase & LayerStyleProperties;
  updateLayerStyle: (
    layerId: string,
    propertyName: keyof LayerStyleProperties,
    value: StyleValue
  ) => void;
  isDisabled: boolean;
}

export const RasterLayoutProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
  if (layer.maplibreLayerType !== 'raster') return null;

  return (
    <>
      {/* visibility */}
      <StylePropertyInput
        key={`${layer.id}-visibility`}
        layerId={layer.id}
        propertyName="visibility"
        label="Visibility"
        currentValue={layer.visibility}
        propertyType="select"
        selectOptions={[{ value: 'visible', label: 'Visible' }, { value: 'none', label: 'None' }]}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />

    </>
  );
};

export const RasterPaintProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
  if (layer.maplibreLayerType !== 'raster') return null;
  return (
    <>
      {/* raster-brightness-max */}
      <StylePropertyInput
        key={`${layer.id}-raster-brightness-max`}
        layerId={layer.id}
        propertyName="rasterBrightnessMax"
        label="Brightness Max"
        currentValue={layer.rasterBrightnessMax}
        propertyType="range"
        numberConfig={{ min: 0, max: 1, step: 0.01 }}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* raster-brightness-min */}
      <StylePropertyInput
        key={`${layer.id}-raster-brightness-min`}
        layerId={layer.id}
        propertyName="rasterBrightnessMin"
        label="Brightness Min"
        currentValue={layer.rasterBrightnessMin}
        propertyType="range"
        numberConfig={{ min: 0, max: 1, step: 0.01 }}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* raster-contrast */}
      <StylePropertyInput
        key={`${layer.id}-raster-contrast`}
        layerId={layer.id}
        propertyName="rasterContrast"
        label="Contrast"
        currentValue={layer.rasterContrast}
        propertyType="range"
        numberConfig={{ min: -1, max: 1, step: 0.01 }}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* raster-fade-duration */}
      <StylePropertyInput
        key={`${layer.id}-raster-fade-duration`}
        layerId={layer.id}
        propertyName="rasterFadeDuration"
        label="Fade Duration (ms)"
        currentValue={layer.rasterFadeDuration}
        propertyType="number"
        numberConfig={{ min: 0, step: 1 }}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* raster-hue-rotate */}
      <StylePropertyInput
        key={`${layer.id}-raster-hue-rotate`}
        layerId={layer.id}
        propertyName="rasterHueRotate"
        label="Hue Rotate (degrees)"
        currentValue={layer.rasterHueRotate}
        propertyType="range"
        numberConfig={{ min: 0, max: 360, step: 1 }}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* raster-opacity */}
      <StylePropertyInput
        key={`${layer.id}-raster-opacity`}
        layerId={layer.id}
        propertyName="rasterOpacity"
        label="Opacity"
        currentValue={layer.rasterOpacity}
        propertyType="range"
        numberConfig={{ min: 0, max: 1, step: 0.01 }}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
        hideNumericValueDisplay={true}
      />
      {/* raster-resampling */}
      <StylePropertyInput
        key={`${layer.id}-raster-resampling`}
        layerId={layer.id}
        propertyName="rasterResampling"
        label="Resampling"
        currentValue={layer.rasterResampling}
        propertyType="select"
        selectOptions={[{ value: 'linear', label: 'Linear' }, { value: 'nearest', label: 'Nearest' }]}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* raster-saturation */}
      <StylePropertyInput
        key={`${layer.id}-raster-saturation`}
        layerId={layer.id}
        propertyName="rasterSaturation"
        label="Saturation"
        currentValue={layer.rasterSaturation}
        propertyType="range"
        numberConfig={{ min: -1, max: 1, step: 0.01 }}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
    </>
  );
};

const RasterStyleEditor: React.FC<StyleEditorSectionProps> = () => {
  // Questo componente non renderizza più direttamente l'intera UI.
  return null;
};

export default RasterStyleEditor;